import { Listbox, Transition } from '@headlessui/react';
import {
  GlobeAltIcon,
  LockClosedIcon,
  SparklesIcon,
  TranslateIcon,
} from '@heroicons/react/outline';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid';
import { Fragment } from 'react';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';

import type { AiModelOption } from '@/constants/aiModels';
import { languages } from '@/constants/languages';

import { useTemplateTranslation } from './kit';

/** The small rounded controls under the fields. */
export const pillClass =
  'relative inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-3 text-[13px] font-medium text-[#3c3c52] ring-1 ring-inset ring-[#0f0f28]/[0.1] transition hover:ring-[#0f0f28]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-white/[0.04] dark:text-[#c4c4da] dark:ring-white/10 dark:hover:ring-white/25';

/**
 * Model menu. Visitors always run the Standard model; picking any other
 * model is an upgrade moment, so it opens the pricing modal instead.
 */
export function ModelPicker({
  options,
  value,
  onLockedPick,
}: {
  options: AiModelOption[];
  value: AiModelOption;
  onLockedPick: (option: AiModelOption) => void;
}) {
  const { t } = useTemplateTranslation();

  return (
    <Listbox
      value={value}
      by='value'
      onChange={(option: AiModelOption) => {
        if (option.value !== value.value) onLockedPick(option);
      }}
    >
      <div className='relative'>
        <Listbox.Label className='sr-only'>{t('AI Model')}</Listbox.Label>
        <Listbox.Button className={pillClass}>
          <SparklesIcon
            className='h-4 w-4 text-primary-600 dark:text-primary-300'
            aria-hidden='true'
          />
          {t(value.title)}
          <ChevronDownIcon
            className={clsxm('-me-0.5 h-4 w-4', tone.quiet)}
            aria-hidden='true'
          />
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave='transition ease-in duration-100'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <Listbox.Options className='absolute start-0 z-30 mt-2 max-h-80 w-[min(20rem,calc(100vw-3rem))] overflow-y-auto overscroll-contain rounded-2xl bg-white p-1.5 shadow-[0_24px_48px_-16px_rgba(15,15,40,0.28)] ring-1 ring-[#0f0f28]/[0.08] focus:outline-none dark:bg-[#1b1b28] dark:ring-white/10'>
            {options.map((option) => {
              const locked = option.value !== value.value;
              return (
                <Listbox.Option
                  key={option.value}
                  value={option}
                  className={({ active }) =>
                    clsxm(
                      'flex cursor-pointer select-none gap-3 rounded-xl px-3 py-2.5',
                      active && 'bg-[#f3f3f9] dark:bg-white/[0.06]',
                    )
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className='min-w-0 flex-1'>
                        <span
                          className={clsxm(
                            'flex items-center gap-2 text-[14px] font-medium',
                            tone.ink,
                          )}
                        >
                          {t(option.title)}
                          {option.isNew ? (
                            <span className='rounded-full bg-primary-50 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:bg-primary-400/15 dark:text-primary-200'>
                              {t('New')}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={clsxm('mt-0.5 block text-xs', tone.quiet)}
                        >
                          {t(option.description)}
                        </span>
                      </span>
                      <span className='mt-0.5 flex-none'>
                        {selected ? (
                          <CheckIcon
                            className='h-5 w-5 text-primary-600 dark:text-primary-300'
                            aria-hidden='true'
                          />
                        ) : locked ? (
                          <LockClosedIcon
                            className={clsxm('h-4 w-4', tone.quiet)}
                            aria-label={t('Upgrade to unlock')}
                          />
                        ) : null}
                      </span>
                    </>
                  )}
                </Listbox.Option>
              );
            })}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

/** The output language, as a native select dressed as a pill. */
export function LanguagePill({
  value,
  onChange,
}: {
  value: string;
  onChange: (language: string) => void;
}) {
  const { t } = useTemplateTranslation();
  return (
    <label className={clsxm(pillClass, 'cursor-pointer pe-2')}>
      <TranslateIcon
        className={clsxm('h-4 w-4 flex-none', tone.quiet)}
        aria-hidden='true'
      />
      <span className='sr-only'>{t('Language')}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className='h-full w-[6.5rem] cursor-pointer appearance-none border-0 bg-transparent bg-none py-0 pe-5 ps-0 text-[13px] font-medium text-inherit [field-sizing:content] focus:outline-none focus:ring-0 supports-[field-sizing:content]:w-auto'
      >
        {Object.values(languages).map((language) => (
          <option key={language} value={language}>
            {t(language)}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        className={clsxm(
          'pointer-events-none absolute end-2 top-1/2 h-4 w-4 -translate-y-1/2',
          tone.quiet,
        )}
        aria-hidden='true'
      />
    </label>
  );
}

/**
 * How many results to write. One is free; asking for more is an upgrade
 * moment, so the count stays at one and the pricing modal opens.
 */
export function OutputsPill({
  value,
  max,
  onChange,
}: {
  value: number;
  max: number;
  onChange: (count: number) => void;
}) {
  const { t } = useTemplateTranslation();
  return (
    <label className={clsxm(pillClass, 'cursor-pointer pe-2')}>
      <span className={tone.quiet}>{t('Results')}</span>
      <select
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className='h-full cursor-pointer appearance-none border-0 bg-transparent bg-none py-0 pe-5 ps-0 text-[13px] font-semibold tabular-nums text-inherit focus:outline-none focus:ring-0'
      >
        {Array.from({ length: Math.max(1, max) }, (_, index) => (
          <option key={index} value={index + 1}>
            {index + 1}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        className={clsxm(
          'pointer-events-none absolute end-2 top-1/2 h-4 w-4 -translate-y-1/2',
          tone.quiet,
        )}
        aria-hidden='true'
      />
    </label>
  );
}

/** Web search is a paid feature: the pill explains and opens pricing. */
export function WebSearchPill({ onClick }: { onClick: () => void }) {
  const { t } = useTemplateTranslation();
  return (
    <button
      type='button'
      onClick={onClick}
      aria-pressed='false'
      title={t(
        'Search Google for current information before writing — recommended for topics about recent events. Available on paid plans.',
      )}
      className={pillClass}
    >
      <GlobeAltIcon
        className={clsxm('h-4 w-4', tone.quiet)}
        aria-hidden='true'
      />
      {t('Search the web')}
      <LockClosedIcon
        className={clsxm('h-3.5 w-3.5', tone.quiet)}
        aria-hidden='true'
      />
    </button>
  );
}
